/**
 * TutorialModal - First-time player onboarding
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSettingsStore } from '../../stores/settingsStore';

const TUTORIAL_STEPS = [
  {
    title: 'Welcome to Anagrabble!',
    content: 'A fast-paced multiplayer word game where you form and steal words.',
    icon: (
      <svg className="w-12 h-12 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    title: 'Flip Tiles',
    content: 'Take turns flipping vowels (left pile) or consonants (right pile). Use arrow keys for shortcuts!',
    icon: (
      <svg className="w-12 h-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
  },
  {
    title: 'Claim Words',
    content: 'Form words (4+ letters) from the middle pool. Type your word and press Enter to claim it!',
    icon: (
      <svg className="w-12 h-12 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
  },
  {
    title: 'Steal Words',
    content: 'Steal or combine words by adding at least one middle tile and rearranging (BOOK + CASE → BOOKCASE doesn\'t count). Tap words to select, then type the new word.',
    icon: (
      <svg className="w-12 h-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
      </svg>
    ),
  },
  {
    title: 'Scoring',
    content: '4 letters = 1pt, 5 = 2pts, 6 = 4pts, 7 = 7pts, 8 = 10pts, 9+ = 15pts (+5 per extra letter).',
    icon: (
      <svg className="w-12 h-12 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
      </svg>
    ),
  },
];

interface TutorialModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TutorialModal({ isOpen, onClose }: TutorialModalProps) {
  const [step, setStep] = useState(0);
  const markTutorialSeen = useSettingsStore((s) => s.markTutorialSeen);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  // Keep latest onClose without re-running the focus effect on every parent render
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Reset step to 0 when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(0);
    }
  }, [isOpen]);

  // Initial focus, Esc to close, simple focus trap, restore focus on close
  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusTimer = window.setTimeout(() => nextButtonRef.current?.focus(), 0);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        markTutorialSeen();
        onCloseRef.current();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [isOpen, markTutorialSeen]);

  const handleNext = () => {
    if (step < TUTORIAL_STEPS.length - 1) {
      setStep(step + 1);
    } else {
      handleFinish();
    }
  };

  const handlePrev = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleFinish = () => {
    markTutorialSeen();
    onClose();
  };

  const currentStep = TUTORIAL_STEPS[step];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={handleFinish}
          />

          {/* Modal - responsive for landscape, properly centered on desktop */}
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="tutorial-title"
            aria-describedby="tutorial-content"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="tutorial-modal fixed inset-4 landscape:inset-y-4 landscape:inset-x-[10%] md:inset-0 md:m-auto md:w-[28rem] md:h-fit md:max-h-[80dvh] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden"
          >
            {/* Skip button */}
            <button
              onClick={handleFinish}
              className="absolute top-2 right-3 landscape:top-1 landscape:right-2 px-2 py-1 text-slate-400 hover:text-slate-600 text-sm z-10"
              aria-label="Skip tutorial"
            >
              Skip
            </button>

            {/* Scrollable content wrapper */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {/* Content - compact in landscape */}
              <div className="flex flex-col items-center justify-center p-6 landscape:p-3 landscape:py-2 text-center min-h-full">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                    className="flex flex-col items-center landscape:flex-row landscape:gap-4 landscape:text-left"
                  >
                    <div className="mb-4 landscape:mb-0 landscape:shrink-0 [&_svg]:w-10 [&_svg]:h-10 landscape:[&_svg]:w-6 landscape:[&_svg]:h-6">
                      {currentStep.icon}
                    </div>
                    <div>
                      <h2 id="tutorial-title" className="text-xl landscape:text-lg font-bold text-slate-900 dark:text-white mb-2 landscape:mb-1">
                        {currentStep.title}
                      </h2>
                      <p id="tutorial-content" className="text-slate-600 dark:text-slate-300 text-base landscape:text-sm leading-relaxed">
                        {currentStep.content}
                      </p>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Progress dots - compact */}
            <div className="flex justify-center gap-0.5 py-1 landscape:py-0 shrink-0">
              {TUTORIAL_STEPS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setStep(i)}
                  className="w-6 h-6 flex items-center justify-center rounded-full"
                  aria-label={`Go to step ${i + 1}`}
                  aria-current={i === step ? 'step' : undefined}
                >
                  <span
                    className={`block w-2 h-2 rounded-full transition-colors ${
                      i === step ? 'bg-blue-500' : 'bg-slate-300 dark:bg-slate-600'
                    }`}
                  />
                </button>
              ))}
            </div>

            {/* Navigation - compact */}
            <div className="flex gap-2 p-3 landscape:p-2 border-t border-slate-200 dark:border-slate-700 shrink-0">
              <button
                onClick={handlePrev}
                disabled={step === 0}
                className="flex-1 py-2 landscape:py-1.5 text-slate-600 dark:text-slate-300 font-medium rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                Back
              </button>
              <button
                ref={nextButtonRef}
                onClick={handleNext}
                className="flex-1 py-2 landscape:py-1.5 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors text-sm"
              >
                {step === TUTORIAL_STEPS.length - 1 ? "Let's Play!" : 'Next'}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
